import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  DialogTitle,
  DialogProps,
} from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import GoalDetailsForm from './GoalDetailsForm';
import GoalDurationForm from './GoalDurationForm';
import GoalIndicatorsForm from './GoalIndicatorsForm';
import { IGoalFormValues } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';
import { DurationType } from 'src/Enum/GoalsAndOperationalPlans';
import { useGoalAndOperationalPlansForm } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider';
import { createNewGoal, mapGoalFormValue } from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ModalGoalValidationSchema } from './validators';

type GoalModalProps = {
  actionType: 'add' | 'edit';
  planIndex: number;
  rowId?: number;
  editData?: IGoalFormValues;
} & DialogProps;

const defaultFormValue: IGoalFormValues = {
  durationType: DurationType.WITHIN_MONTH,
  indicators: [{}],
};

const GoalModal: React.FC<GoalModalProps> = ({
  actionType,
  planIndex,
  rowId,
  editData,
  onClose,
  ...restProps
}) => {
  const initialValue =
    actionType === 'edit' && editData
      ? mapGoalFormValue(editData)
      : defaultFormValue;

  const goalContext = useForm<IGoalFormValues>({
    defaultValues: initialValue,
    resolver: zodResolver(ModalGoalValidationSchema),
    mode: 'onChange',
  });

  const { reset, handleSubmit, formState } = goalContext;
  const { setValue, control, trigger } = useGoalAndOperationalPlansForm();

  const goals = useWatch({
    name: `companyValueIncreasePlans.valueUpPlans.${planIndex}.goals`,
  });

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
    reset();
  };

  const handleSave = (data: IGoalFormValues) => {
    const currentGoals = goals || [];
    const updatedGoals = [...currentGoals];
    const newGoal = createNewGoal(data);

    if (actionType === 'add') {
      updatedGoals.push(newGoal);
    }

    if (actionType === 'edit' && rowId !== undefined && rowId !== null) {
      updatedGoals[rowId] = newGoal;
    }

    setValue(
      `companyValueIncreasePlans.valueUpPlans.${planIndex}.goals`,
      updatedGoals
    );
    trigger(`companyValueIncreasePlans.valueUpPlans.${planIndex}.goals`);
    handleClose();
  };

  return (
    <Dialog {...restProps} fullWidth maxWidth="md">
      <DialogTitle fontWeight={'bold'} fontSize={16}>
        {actionType === 'add' ? 'เพิ่ม' : 'แก้ไข'}
        เป้าหมาย
      </DialogTitle>
      <FormProvider {...goalContext}>
        <DialogContent sx={{ padding: 2 }}>
          <GoalDetailsForm goalsOperationalPlansFormControl={control} />
          <GoalDurationForm goalsOperationalPlansFormControl={control} />
          <GoalIndicatorsForm goalsOperationalPlansFormControl={control} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit(handleSave)}
            disabled={!formState.isValid}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default GoalModal;
